import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { PaymentMethodType, postCreatePaymentIntent } from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { PAYMENT_INTENT_CONTAINER_ID } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/CheckoutSessionDetail';
import { usePaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/hooks/usePaymentIntent';
import { CreditCardPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/_shared/entities/PaymentMethodDetail/CreditCardPaymentMethodDetailEntity';
import { ConditionsAndProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/ConditionsAndProgress/ConditionsAndProgress';
import { ContinueCreditCardPurchase } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/ContinueCreditCardPurchase';
import { useMountCreditCardPaymentIntentMutation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/hooks/useMountCreditCardPaymentIntentMutation';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { isIbcStandsAgentUser } from '@/hooks/layoutConstructor/iberiaUserHelpers';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface CreditCardPaymentMethodProps {
  paymentMethodType: PaymentMethodType;
}

export const CreditCardPaymentMethod = ({ paymentMethodType }: CreditCardPaymentMethodProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();
  const { refreshPaymentMethodEntity } = usePaymentIntent();
  const { customBorderRadius } = useCustomTheme();

  const paymentMethodDetailEntity = useMemo(
    () =>
      paymentMethodDetailEntities.find((pme) => pme.type === paymentMethodType) as
        | CreditCardPaymentMethodDetailEntity
        | undefined,
    [paymentMethodDetailEntities, paymentMethodType],
  );

  if (paymentMethodDetailEntity?.error) {
    return (
      <CustomAlert
        title="CREDIT_CARD_FETCH_ERROR_TITLE"
        description="CREDIT_CARD_FETCH_ERROR_DESCRIPTION"
        severity="warning"
      >
        <Box pt={'10px'} />
        <StandardButton main size="small" onClick={() => refreshPaymentMethodEntity(paymentMethodDetailEntity)}>
          <Text tId="CREDIT_CARD_FETCH_ERROR_BTN_LABEL" />
        </StandardButton>
      </CustomAlert>
    );
  }

  if (!paymentMethodDetailEntity || !paymentMethodDetailEntity.isRefreshed) {
    return (
      <Skeleton variant="rect" width={'100%'} height={'400px'} style={{ borderRadius: customBorderRadius.paper }} />
    );
  }

  const shouldCreatePaymentIntent = paymentMethodDetailEntity.shouldCreatePaymentIntent();

  if (shouldCreatePaymentIntent) {
    return <InitCreditCardPaymentIntent paymentMethodDetailEntity={paymentMethodDetailEntity} />;
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gridGap={24}>
      <ConditionsAndProgress paymentMethodDetailEntity={paymentMethodDetailEntity} />
      {paymentMethodDetailEntity.creditCard && (
        <ContinueCreditCardPurchase paymentMethodDetailEntity={paymentMethodDetailEntity} />
      )}
    </Box>
  );
};

const InitCreditCardPaymentIntent = ({
  paymentMethodDetailEntity,
}: {
  paymentMethodDetailEntity: CreditCardPaymentMethodDetailEntity;
}) => {
  const { createPaymentIntent } = usePaymentIntent();
  const { userEntity } = useUserContext();
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();

  const mountCreditCardPaymentIntentMutation = useMountCreditCardPaymentIntentMutation({
    domSelector: `#${PAYMENT_INTENT_CONTAINER_ID}`,
    paymentMethodDetailEntity,
  });

  const [isLoading, setIsLoading] = useState(false);

  // The stands application flow already creates the payment intent; if that failed, this button creates it.
  // Stands agents see the same texts as the "continue" panel so both cases look the same to them.
  const isStandsAgent = !!userEntity && isIbcStandsAgentUser(userEntity);

  const handleStart = async () => {
    setIsLoading(true);
    try {
      await createPaymentIntent(paymentMethodDetailEntity);
      await mountCreditCardPaymentIntentMutation.mutateAsync();
    } catch (error) {
      setSnackbarNotification({ msg: `${postCreatePaymentIntent.name}_rejected`, level: 'error', error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomPaper fill="paper">
      <Box gridGap={24} display="flex" justifyContent="space-between" alignItems="flex-start">
        <FlexColumn gridGap={4}>
          <Font variant="subtitle1" color="textPrimary" display="block">
            <Text strong tId={isStandsAgent ? 'CREDIT_CARD_PURCHASE_TITLE' : 'CREDIT_CARD_START_PAYMENT_METHOD'} />
          </Font>
          <Font variant="body2" color="textPrimary">
            <Text
              tId={
                isStandsAgent ? 'CREDIT_CARD_PURCHASE_FIRST_INSTRUCTIONS' : 'CREDIT_CARD_START_PAYMENT_METHOD_DESCRIPTION'
              }
            />
          </Font>
        </FlexColumn>
        <Box flexShrink={0}>
          <StandardButton main size="small" loading={isLoading} onClick={handleStart}>
            <Text tId={isStandsAgent ? 'CREDIT_CARD_PURCHASE_CONTINUE' : 'START'} />
          </StandardButton>
        </Box>
      </Box>
    </CustomPaper>
  );
};
